// @ts-nocheck
/**
 * Product-level free lamp. Not the LCP path.
 * Deferred; reconciles after cart:update. No layout reads, no per-item listeners.
 * Lamp qty = sum of tier(compare-at price) × sellable line quantity. One lamp line for the whole cart.
 * A line with no compare-at price uses its selling price for the tier.
 * The cart keeps the requested quantity. Checkout's seller proposal is the quantity that
 * actually sells, so a line above that cap must not earn lamps for the extra units.
 * The cart is read again after that checkout request, because the request can lower a line
 * to the units in stock. Lamps use that later quantity. If checkout does not report a cap,
 * lamps follow the quantity still in the cart. Buy Now still removes the line when stock
 * cannot be confirmed.
 * Lines already 50%+ off (Diwali50%off tag, compare-at math, or _equalorabove50%discount) earn none.
 */
(function freeLampSync() {
  const SYNC_SOURCE = 'free-lamp-sync';
  const MARKER = '_is_lamp_freebie';
  const LAMP_ITEM_CODE = '19GFT0007';
  const PROP_50_COMPARE = '_equalorabove50%discount';
  const DIWALI_HALF_OFF_TAG = 'diwali50%off';

  function cfg() {
    return window.FREE_LAMP_CONFIG || {};
  }

  function isTruthy(val) {
    return val === true || val === 'true' || val === 'True';
  }

  function lampQtyForUnit(priceAed) {
    if (typeof window.getFreeLampQty === 'function') return window.getFreeLampQty(priceAed);
    return 0;
  }

  function isLampLine(item) {
    return isTruthy((item?.properties || {})[MARKER]);
  }

  function lampLineProperties() {
    return {
      [MARKER]: 'true',
      '_Item code': LAMP_ITEM_CODE,
    };
  }

  function lampLineHasItemCode(item) {
    return String((item?.properties || {})['_Item code'] || '') === LAMP_ITEM_CODE;
  }

  function isSkippedParent(item, lampVariantId, cabinVariantId) {
    if (!item || isLampLine(item)) return true;
    const props = item.properties || {};
    if (isTruthy(props._is_buyxgety_freebie)) return true;
    if (isTruthy(props._auto_subtotal_freebie)) return true;
    const variantId = Number(item.variant_id);
    if (lampVariantId && variantId === lampVariantId) return true;
    if (cabinVariantId && variantId === cabinVariantId) return true;
    return false;
  }

  function parseProductTags(raw) {
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map((tag) => String(tag).trim()).filter(Boolean) : [];
    } catch (error) {
      return String(raw)
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
  }

  function catalogDiscountPct(compareAtCents, unitCents) {
    const compareAt = Number(compareAtCents) || 0;
    const unit = Number(unitCents) || 0;
    if (compareAt <= 0 || unit <= 0 || compareAt <= unit) return 0;
    return Math.round(((compareAt - unit) * 100) / compareAt);
  }

  function readCartLineDiscountMeta() {
    const meta = new Map();
    document.querySelectorAll('[data-line-key][data-compare-at], [data-line-key][data-product-tags]').forEach((el) => {
      const lineKey = el.getAttribute('data-line-key');
      if (!lineKey) return;
      const prev = meta.get(lineKey) || { compareAt: 0, diwaliTag: false };
      const compareAt = Number(el.getAttribute('data-compare-at')) || 0;
      if (compareAt > prev.compareAt) prev.compareAt = compareAt;
      if (!prev.diwaliTag) {
        prev.diwaliTag = parseProductTags(el.getAttribute('data-product-tags')).some(
          (tag) => tag.toLowerCase() === DIWALI_HALF_OFF_TAG
        );
      }
      meta.set(lineKey, prev);
    });
    return meta;
  }

  function isHalfOffMerchandise(item, lineMeta) {
    if (isTruthy((item?.properties || {})[PROP_50_COMPARE])) return true;
    const meta = lineMeta.get(String(item?.key || ''));
    if (!meta) return false;
    if (meta.diwaliTag) return true;
    const unitCents = Number(item.original_price || item.price);
    return catalogDiscountPct(meta.compareAt, unitCents) >= 50;
  }

  const compareAtByVariant = new Map();
  const compareAtRequests = new Map();

  function sellingCents(item) {
    return Number(item?.original_price || item?.price) || 0;
  }

  async function compareAtCentsForItem(item, lineMeta) {
    const variantId = Number(item?.variant_id);
    const meta = lineMeta.get(String(item?.key || ''));
    if (meta) {
      const fromCart = Number(meta.compareAt) || 0;
      if (variantId) compareAtByVariant.set(variantId, fromCart);
      return fromCart;
    }
    if (!variantId) return 0;
    if (compareAtByVariant.has(variantId)) return compareAtByVariant.get(variantId);
    if (compareAtRequests.has(variantId)) return compareAtRequests.get(variantId);

    const handle = item.handle;
    const request = (async () => {
      if (!handle) return 0;
      try {
        const product = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
          headers: { Accept: 'application/json' },
        }).then((response) => (response.ok ? response.json() : null));
        const variant = (product?.variants || []).find((entry) => Number(entry.id) === variantId);
        return Number(variant?.compare_at_price) || 0;
      } catch (error) {
        return 0;
      }
    })();
    compareAtRequests.set(variantId, request);
    try {
      const compareAt = await request;
      compareAtByVariant.set(variantId, compareAt);
      return compareAt;
    } finally {
      compareAtRequests.delete(variantId);
    }
  }

  function tierPriceAed(item, compareAtCents) {
    const basis = Number(compareAtCents) > 0 ? Number(compareAtCents) : sellingCents(item);
    return Math.floor(basis / 100);
  }

  function getSectionsPayload() {
    const sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((node) => {
      const sectionId = node.getAttribute('data-section-id');
      if (sectionId && sectionIds.indexOf(sectionId) === -1) sectionIds.push(sectionId);
    });
    return {
      sections: sectionIds.join(','),
      sections_url: window.location.pathname,
    };
  }

  function getCheckoutButtons() {
    const byId = document.querySelectorAll('#checkout');
    if (byId.length) return Array.from(byId);
    return Array.from(document.querySelectorAll('button.cart__checkout-button[name="checkout"]'));
  }

  function setCheckoutLocked(locked) {
    getCheckoutButtons().forEach((btn) => {
      if (locked) {
        if (btn.dataset.freeLampCheckoutPrevDisabled === undefined) {
          btn.dataset.freeLampCheckoutPrevDisabled = btn.disabled ? '1' : '0';
        }
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
      } else if (btn.dataset.freeLampCheckoutPrevDisabled !== undefined) {
        btn.disabled = btn.dataset.freeLampCheckoutPrevDisabled === '1';
        delete btn.dataset.freeLampCheckoutPrevDisabled;
        btn.removeAttribute('aria-busy');
      }
    });

    document.querySelectorAll('.cart-drawer__footer-view-buttons').forEach((el) => {
      if (locked) {
        if (el.dataset.freeLampFooterPointerEvents === undefined) {
          el.dataset.freeLampFooterPointerEvents = el.style.pointerEvents || '';
        }
        el.style.pointerEvents = 'none';
      } else if (el.dataset.freeLampFooterPointerEvents !== undefined) {
        el.style.pointerEvents = el.dataset.freeLampFooterPointerEvents;
        delete el.dataset.freeLampFooterPointerEvents;
      }
    });
  }

  const CHECKOUT_POINTER_SAFETY_UNLOCK_MS = 3500;
  let checkoutPointerSafetyTimer = null;

  function clearCheckoutPointerSafetyTimer() {
    if (checkoutPointerSafetyTimer != null) {
      clearTimeout(checkoutPointerSafetyTimer);
      checkoutPointerSafetyTimer = null;
    }
  }

  function scheduleCheckoutPointerSafetyUnlock() {
    clearCheckoutPointerSafetyTimer();
    checkoutPointerSafetyTimer = setTimeout(() => {
      checkoutPointerSafetyTimer = null;
      setCheckoutLocked(false);
    }, CHECKOUT_POINTER_SAFETY_UNLOCK_MS);
  }

  async function postJson(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok && !data.status, data };
  }

  function publishCartUpdate(cart, sections) {
    const itemCount = Number(cart?.item_count ?? 0);
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: SYNC_SOURCE,
          data: {
            itemCount,
            source: SYNC_SOURCE,
            sections: sections || {},
          },
        },
      })
    );
    if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      publish(window.PUB_SUB_EVENTS.cartUpdate, {
        source: SYNC_SOURCE,
        cartData: cart,
        itemCount,
        sections: sections || {},
      });
    }
  }

  function withSections(body, sectionsPayload) {
    if (!sectionsPayload?.sections) return body;
    body.sections = sectionsPayload.sections;
    body.sections_url = sectionsPayload.sections_url;
    return body;
  }

  function isDigit(code) {
    return code >= 48 && code <= 57;
  }

  function readSellerLineQty(windowText) {
    const marks = ['quantity":{"items":{"value":'];
    for (const mark of marks) {
      const at = windowText.indexOf(mark);
      if (at < 0) continue;
      const numStart = at + mark.length;
      let numEnd = numStart;
      while (numEnd < windowText.length && isDigit(windowText.charCodeAt(numEnd))) numEnd += 1;
      if (numEnd === numStart) continue;
      const qty = Number(windowText.slice(numStart, numEnd));
      if (Number.isFinite(qty)) return qty;
    }
    return null;
  }

  function sellerQtyByVariant(html) {
    const normalized = html.replace(/\\u0026quot;/g, '"').replace(/\\u0026/g, '&').replace(/&quot;/g, '"');
    const start = normalized.indexOf('sellerProposal');
    if (start < 0) return null;
    const slice = normalized.slice(start);
    const caps = new Map();
    const variantMark = 'ProductVariant/';
    let from = 0;
    while (from < slice.length) {
      const variantAt = slice.indexOf(variantMark, from);
      if (variantAt < 0) break;
      const idStart = variantAt + variantMark.length;
      let idEnd = idStart;
      while (idEnd < slice.length && isDigit(slice.charCodeAt(idEnd))) idEnd += 1;
      if (idEnd === idStart) {
        from = variantAt + variantMark.length;
        continue;
      }
      const id = Number(slice.slice(idStart, idEnd));
      const nextVariant = slice.indexOf(variantMark, idEnd);
      const windowEnd = nextVariant < 0 ? Math.min(slice.length, idEnd + 6000) : nextVariant;
      const qty = readSellerLineQty(slice.slice(idEnd, windowEnd));
      if (id && qty != null) caps.set(id, qty);
      from = idEnd;
    }
    return caps.size ? caps : null;
  }

  async function loadSellerQtyCaps() {
    const once = async () => {
      try {
        const response = await fetch('/checkout', {
          headers: { Accept: 'text/html' },
          redirect: 'follow',
          cache: 'no-store',
        });
        if (!response.ok) return null;
        const html = await response.text();
        return sellerQtyByVariant(html);
      } catch (error) {
        return null;
      }
    };
    const first = await once();
    if (first) return first;
    await new Promise((resolve) => setTimeout(resolve, 200));
    return once();
  }

  function consumeSellableQty(item, remainingByVariant) {
    const qty = Math.max(0, Number(item.quantity) || 0);
    if (!remainingByVariant) return qty;
    const variantId = Number(item.variant_id);
    if (!remainingByVariant.has(variantId)) return qty;
    const cap = remainingByVariant.get(variantId);
    if (cap == null) return qty;
    const sellable = Math.min(qty, Math.max(0, cap));
    remainingByVariant.set(variantId, cap - sellable);
    return sellable;
  }

  /**
   * @param {{ publish?: boolean }} [options]
   */
  async function syncLamps(options) {
    const config = cfg();
    const lampVariantId = config.variantId ? Number(config.variantId) : 0;
    if (!config.enabled || !lampVariantId) return;

    const cabinVariantId = Number(window.CART_REWARD_CONFIG?.cabinBagVariantId) || 0;
    let cart = await fetch('/cart.js').then((response) => response.json()).catch(() => null);
    if (!cart?.items) return;

    const lineDiscountMeta = readCartLineDiscountMeta();
    const parentLines = (source) => {
      const lines = [];
      for (const item of source?.items || []) {
        if (isSkippedParent(item, lampVariantId, cabinVariantId)) continue;
        if (isHalfOffMerchandise(item, lineDiscountMeta)) continue;
        lines.push(item);
      }
      return lines;
    };

    let parents = parentLines(cart);
    let remainingByVariant = null;
    if (parents.some((item) => Number(item.quantity) > 1)) {
      remainingByVariant = await loadSellerQtyCaps();
      // Opening checkout can lower a line to the units actually available.
      // The cart read from before that request still has the typed quantity.
      const latest = await fetch('/cart.js').then((response) => response.json()).catch(() => null);
      if (latest?.items) {
        cart = latest;
        parents = parentLines(cart);
      }
    }

    const unconfirmed = parents.some((item) => Number(item.quantity) > 1) && !remainingByVariant;
    if (unconfirmed && !options?.publish) {
      // Buy Now emptied the cart before this add. Clear it again so a refresh
      // cannot grant lamps for the typed quantity. Add to cart keeps the line.
      if (!options?.captureSections) {
        await postJson('/cart/clear.js', {});
        const cleared = await fetch('/cart.js').then((response) => response.json()).catch(() => null);
        if (cleared) publishCartUpdate(cleared, {});
      }
      return {
        blocked: true,
        message: 'Stock could not be confirmed. Please try Buy Now again.',
      };
    }
    // Cart and page-load reconcile: a missing checkout cap must not freeze the lamp
    // line at the previous quantity. The watches already in the cart still earn lamps.

    const lampLines = (cart.items || []).filter(isLampLine);
    const toAdd = [];
    const updates = {};
    let lampPropertyFix = null;
    let desiredQty = 0;
    let clampedTo = null;
    const compareAtByLine = new Map();
    await Promise.all(
      parents.map(async (item) => {
        compareAtByLine.set(item, await compareAtCentsForItem(item, lineDiscountMeta));
      })
    );

    for (const item of parents) {
      const qty = Math.max(0, Number(item.quantity) || 0);
      const sellable = consumeSellableQty(item, remainingByVariant);
      if (item.key && sellable !== qty) updates[item.key] = sellable;
      if (sellable < qty) clampedTo = sellable;
      if (!sellable) continue;
      const perUnit = lampQtyForUnit(tierPriceAed(item, compareAtByLine.get(item)));
      if (perUnit <= 0) continue;
      desiredQty += perUnit * sellable;
    }

    if (!lampLines.length) {
      if (desiredQty > 0) {
        toAdd.push({
          id: lampVariantId,
          quantity: desiredQty,
          properties: lampLineProperties(),
        });
      }
    } else {
      const primaryLamp = lampLines[0];
      const lampQtyDiffers = Number(primaryLamp.quantity || 0) !== desiredQty;
      if (!lampLineHasItemCode(primaryLamp)) {
        lampPropertyFix = {
          id: primaryLamp.key,
          quantity: desiredQty,
          properties: lampLineProperties(),
        };
      } else if (lampQtyDiffers) {
        updates[primaryLamp.key] = desiredQty;
      }
      for (let i = 1; i < lampLines.length; i += 1) updates[lampLines[i].key] = 0;
    }

    if (!toAdd.length && !Object.keys(updates).length && !lampPropertyFix) return;

    const sectionsPayload = getSectionsPayload();
    let sections = {};
    const hasUpdates = Object.keys(updates).length > 0;

    if (toAdd.length) {
      const body = { items: toAdd };
      // Buy Now must not depend on cart section rendering. A failed section render
      // rejects the whole add, and checkout then opens with no lamp.
      if ((options?.publish || options?.captureSections) && !hasUpdates) withSections(body, sectionsPayload);
      const added = await postJson('/cart/add.js', body);
      if (!added.ok) {
        console.warn('free-lamp-sync add failed', added.data);
        return;
      }
      if (added.data?.sections) sections = added.data.sections;
    }

    if (hasUpdates) {
      const updated = await postJson('/cart/update.js', withSections({ updates }, sectionsPayload));
      if (!updated.ok) return;
      if (updated.data?.sections) sections = updated.data.sections;
    }

    if (lampPropertyFix) {
      const changeBody =
        options?.publish || options?.captureSections
          ? withSections(lampPropertyFix, sectionsPayload)
          : lampPropertyFix;
      const changed = await postJson('/cart/change.js', changeBody);
      if (!changed.ok) {
        console.warn('free-lamp-sync item code failed', changed.data);
        return;
      }
      if (changed.data?.sections) sections = changed.data.sections;
    }

    const fresh =
      options?.publish || options?.captureSections
        ? await fetch('/cart.js').then((response) => response.json()).catch(() => null)
        : null;

    if (options?.publish && fresh) publishCartUpdate(fresh, sections);

    return {
      blocked: false,
      adjusted: clampedTo != null,
      sellable: clampedTo == null ? null : Math.max(0, clampedTo),
      sections,
      cart: fresh,
    };
  }

  function showBuyNowStockError(message) {
    const roots = document.querySelectorAll('product-form-component, .sticky-add-to-cart');
    roots.forEach((root) => {
      const errorEl = root.querySelector('.product-form-text__error');
      if (!(errorEl instanceof HTMLElement)) return;
      errorEl.classList.remove('hidden');
      let messageEl = errorEl.querySelector('[data-error-message]');
      if (!(messageEl instanceof HTMLElement)) {
        messageEl = document.createElement('span');
        messageEl.className = 'product-form-text__error-message';
        messageEl.setAttribute('data-error-message', '');
        errorEl.appendChild(messageEl);
      }
      messageEl.textContent = message;
    });
    document.querySelector('.sticky-add-to-cart__bar')?.classList.add('sticky-add-to-cart__bar--error-visible');
  }

  function setPdpQuantity(qty) {
    document.querySelectorAll('input[name="quantity"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (input.closest('cart-items-component, cart-quantity-selector-component')) return;
      input.value = String(qty);
    });
  }

  window.applyBuyNowStockResult = function (result) {
    if (!result?.blocked) return false;
    showBuyNowStockError(result.message || 'Only the available quantity can be purchased.');
    if (Number.isFinite(result.sellable) && result.sellable > 0) setPdpQuantity(result.sellable);
    return true;
  };

  let lampReconcileQueue = Promise.resolve();

  function scheduleLampReconcile() {
    const job = lampReconcileQueue.then(async () => {
      clearCheckoutPointerSafetyTimer();
      setCheckoutLocked(true);
      try {
        await syncLamps({ publish: true });
      } catch (error) {
        console.warn('free-lamp-sync', error);
      } finally {
        setCheckoutLocked(false);
      }
    });
    lampReconcileQueue = job.catch(() => {});
    return job;
  }

  window.syncProductLampFreebies = function () {
    const job = lampReconcileQueue.then(() => syncLamps({ publish: false }));
    lampReconcileQueue = job.catch(() => {});
    return job;
  };

  window.syncAddedLineToSellable = function () {
    const job = lampReconcileQueue.then(async () => {
      setCheckoutLocked(true);
      try {
        return await syncLamps({ publish: false, captureSections: true });
      } finally {
        setCheckoutLocked(false);
      }
    });
    lampReconcileQueue = job.catch(() => {});
    return job;
  };

  function sourceFromEvent(event) {
    return event?.detail?.data?.source || event?.source || event?.detail?.sourceId || '';
  }

  function onCartUpdate(event) {
    if (sourceFromEvent(event) === SYNC_SOURCE) return;
    scheduleLampReconcile();
  }

  function scheduleInitialLampReconcile() {
    const run = function () {
      scheduleLampReconcile();
    };
    if (typeof window.runAfterLcp === 'function') {
      window.runAfterLcp(run);
      return;
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run, { once: true });
    } else {
      run();
    }
  }

  scheduleInitialLampReconcile();

  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
    subscribe(window.PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (sourceFromEvent(event) === SYNC_SOURCE) return;
      scheduleLampReconcile();
    });
  }

  document.addEventListener('cart:update', onCartUpdate);

  /**
   * Shopify starts checkout on pointerdown. A fast click can open checkout with the
   * typed quantity before this script reads the quantity checkout will actually sell.
   * One flight: stop that pointerdown, then redirect only after lamps match that quantity.
   */
  let buyNowFlight = null;

  function startGuardedBuyNow(button, start) {
    if (buyNowFlight) return;
    buyNowFlight = Promise.resolve()
      .then(start)
      .finally(() => {
        buyNowFlight = null;
        button.disabled = false;
      });
    button.disabled = true;
  }

  function handleBuyNowPress(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const button = target.closest('.shopify-payment-button__button, .sticky-add-to-cart__button--buy-now');
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.classList.contains('shopify-payment-button__button--branded')) return;
    if (button.disabled && !buyNowFlight) return;

    const onclick = button.getAttribute('onclick') || '';
    const isCustomBuyNow = /buyxgetyBuyNow|freebieBuyNow|cartRewardBuyNow/.test(onclick);
    const isNativeBuyNow =
      button.classList.contains('shopify-payment-button__button') &&
      !onclick &&
      !button.closest('.shopify-payment-button__more-options');
    if (!isCustomBuyNow && !isNativeBuyNow) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (buyNowFlight) return;

    startGuardedBuyNow(button, function () {
      if (onclick.includes('freebieBuyNow') && typeof window.freebieBuyNow === 'function') {
        return window.freebieBuyNow();
      }
      if (onclick.includes('cartRewardBuyNow') && typeof window.cartRewardBuyNow === 'function') {
        return window.cartRewardBuyNow();
      }
      if (onclick.includes('buyxgetyBuyNow') && typeof window.buyxgetyBuyNow === 'function') {
        return window.buyxgetyBuyNow();
      }
      if (isNativeBuyNow) return lampProductBuyNow();
    });
  }

  window.addEventListener('pointerdown', handleBuyNowPress, true);
  window.addEventListener('click', handleBuyNowPress, true);

  async function lampProductBuyNow() {
    const form =
      document.querySelector('.main-product-page product-form-component form[data-type="add-to-cart-form"]') ||
      document.querySelector('product-form-component form[data-type="add-to-cart-form"]');
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const fd = new FormData(form);
    const variantId = Number(fd.get('id'));
    if (!variantId) return;
    fd.set('quantity', String(Math.max(1, Number(fd.get('quantity')) || 1)));

    try {
      const clearRes = await fetch('/cart/clear.js', { method: 'POST', headers: { Accept: 'application/json' } });
      if (!clearRes.ok) return;

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        body: fd,
        headers: { Accept: 'application/json' },
      });
      const addJson = await addRes.json().catch(() => ({}));
      if (!addRes.ok || addJson.status) {
        console.warn('free-lamp buy now add failed', addJson);
        return;
      }

      const stock = await syncLamps({ publish: false });
      if (window.applyBuyNowStockResult(stock)) return;
      window.location.assign('/checkout');
    } catch (error) {
      console.warn('free-lamp buy now', error);
    }
  }

  document.addEventListener(
    'pointerdown',
    function (event) {
      if (!cfg().enabled) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('cart-items-component')) return;
      const isRemove = target.closest('button.cart-items__remove');
      const isQtyButton =
        target.closest('cart-quantity-selector-component button') ||
        target.closest('quantity-selector-component button');
      if (!isRemove && !isQtyButton) return;
      setCheckoutLocked(true);
      scheduleCheckoutPointerSafetyUnlock();
    },
    true
  );
})();
